/**
 * Main Theme Provider Component
 * 
 * This component provides dynamic theming based on:
 * - Plant type (JLR vs Standard)
 * - Theme mode (Dark vs Light)
 * - Resolution scaling (Laptop → 4K+)
 * 
 * Features:
 * - Automatic plant-based theme switching
 * - JLR luxury automotive styling
 * - Standard professional styling for other plants
 * - Dark/Light mode support
 * - Material-UI component overrides
 * - **NEW**: Resolution-based scaling for 4K displays and assembly line environments
 * 
 * Resolution Scaling:
 * - Laptop (1536px): 1.0x scale
 * - FHD (1920px): 1.2x scale
 * - QHD (2560px): 1.5x scale
 * - 4K (3200px): 2.0x scale
 * - 4K+ (3840px): 2.5x scale (Assembly line - visible from 7 meters)
 */

import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {CssBaseline} from '@mui/material';
import {
  createTheme,
  ThemeProvider as MUIThemeProvider,
  StyledEngineProvider,
} from '@mui/material/styles';
import typography from './typography';
import breakpoints from './breakpoints';
import {defaultPalette, jlrPalette } from './palette';
import componentsOverride from './overrides';
import { DARK_MODE, JLR_PLANT } from '../constants';
import React from 'react';
import {
  getScaleFactor,
  getResolutionTier,
  getComponentScale,
  CSS_VARIABLES,
  COMPONENT_SCALE_MULTIPLIERS,
  DEFAULT_SCALE_VALUES,
} from './resolutionConfig';

// Redux state mapping - connects theme and user data
const mapStateToProps = state => {
  return {
    mode: state.theme,
    plantName: state.user.plant
  };
};

/**
 * Plant-based Palette Selector
 * 
 * Determines which color palette to use based on plant type:
 * - JLR plants get luxury automotive styling (jlrPalette)
 * - Other plants get standard professional styling (defaultPalette)
 * 
 * @param {string} plantName - The plant name from user state
 * @returns {Object} - The appropriate palette object
 */
const setPaletteAccordingToPlant = (plantName) => {
  if (plantName === JLR_PLANT)
    return jlrPalette
  return defaultPalette
}

/**
 * Main Theme Provider Component
 * 
 * @param {Object} mode - Theme mode object from Redux state
 * @param {string} plantName - Plant name from user state
 * @param {React.ReactNode} children - Child components
 */
export const ThemeProvider = ({mode, plantName, children}) => {
  const {themeMode} = mode;
  const isDark = themeMode === DARK_MODE;

  // ============================================
  // RESOLUTION SCALING STATE
  // ============================================
  const [scaleFactor, setScaleFactor] = React.useState(
    typeof window !== 'undefined' ? getScaleFactor(window.innerWidth) : DEFAULT_SCALE_VALUES.scaleFactor
  );
  
  const [resolutionTier, setResolutionTier] = React.useState(
    typeof window !== 'undefined' ? getResolutionTier(window.innerWidth) : 'laptop'
  );

  // ============================================
  // INJECT CSS VARIABLES FOR RESOLUTION SCALING
  // ============================================
  React.useEffect(() => {
    const updateResolutionScaling = () => {
      const width = window.innerWidth;
      const newScaleFactor = getScaleFactor(width);
      const newTier = getResolutionTier(width);
      
      setScaleFactor(newScaleFactor);
      setResolutionTier(newTier);
      
      const root = document.documentElement;
      
      // Base scale factor
      root.style.setProperty(CSS_VARIABLES.SCALE_FACTOR, newScaleFactor.toString());
      root.style.setProperty(CSS_VARIABLES.RESOLUTION_TIER, `"${newTier}"`);
      
      // Font scaling
      root.style.setProperty(CSS_VARIABLES.FONT_SCALE, newScaleFactor.toString());
      
      // Spacing scaling
      root.style.setProperty(CSS_VARIABLES.SPACING_SCALE, newScaleFactor.toString());
      
      // Icon scaling (with multiplier)
      const iconScale = newScaleFactor * COMPONENT_SCALE_MULTIPLIERS.iconSize;
      root.style.setProperty(CSS_VARIABLES.ICON_SCALE, iconScale.toString());
      
      // Border scaling (with multiplier - don't scale as much)
      const borderScale = newScaleFactor * COMPONENT_SCALE_MULTIPLIERS.borderWidth;
      root.style.setProperty(CSS_VARIABLES.BORDER_SCALE, borderScale.toString());
      
      // Table scaling (important for assembly line)
      const tableScale = newScaleFactor * COMPONENT_SCALE_MULTIPLIERS.tableCell;
      root.style.setProperty(CSS_VARIABLES.TABLE_SCALE, tableScale.toString());
      
      // Button scaling
      const buttonScale = newScaleFactor * COMPONENT_SCALE_MULTIPLIERS.button;
      root.style.setProperty(CSS_VARIABLES.BUTTON_SCALE, buttonScale.toString());
      
      // Input scaling
      const inputScale = newScaleFactor * COMPONENT_SCALE_MULTIPLIERS.input;
      root.style.setProperty(CSS_VARIABLES.INPUT_SCALE, inputScale.toString());
      
      // Minimum touch target (44px base, scaled up)
      const minTouchTarget = 44 * newScaleFactor * COMPONENT_SCALE_MULTIPLIERS.minTouchTarget;
      root.style.setProperty(CSS_VARIABLES.MIN_TOUCH_TARGET, `${minTouchTarget}px`);
      
      // Log for debugging (can be removed in production)
      if (process.env.NODE_ENV === 'development') {
        
      }
    };

    // Initial update
    updateResolutionScaling();

    // Update on window resize (debounced)
    let timeoutId = null;
    const handleResize = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(updateResolutionScaling, 150);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []); // Run once on mount and set up resize listener

  // ============================================
  // THEME CREATION
  // ============================================
  // Get the appropriate palette based on plant type
  const palette = setPaletteAccordingToPlant(plantName);

  // Create base theme with typography, breakpoints, and palette
  let theme = createTheme({
    typography,
    breakpoints,
    palette: isDark ? palette.dark : palette.light,
  });

  // Enhance theme with additional properties
  theme = createTheme(theme, {
    // Remove shadows for dark mode (cleaner look)
    shadows: isDark ? [...Array(25).fill('none')] : theme.shadows,
    // Add plant name for component overrides to detect JLR vs Standard
    plantName: plantName,
    // Add resolution info to theme (can be used by components)
    resolution: {
      scaleFactor,
      tier: resolutionTier,
      is4K: scaleFactor >= 2.0,
      isAssemblyLine: scaleFactor >= 2.5,
    },
  });

  // Apply Material-UI component overrides for consistent styling
  theme.components = componentsOverride(theme);

  // ============================================
  // APPLY DATA ATTRIBUTES TO DOCUMENT
  // ============================================
  React.useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', themeMode);
    root.setAttribute('data-resolution-tier', resolutionTier);
    root.setAttribute('data-scale-factor', scaleFactor.toString());
  }, [themeMode, resolutionTier, scaleFactor]);

  return (
    <StyledEngineProvider injectFirst>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </StyledEngineProvider>
  );
};

ThemeProvider.propTypes = {
  mode: PropTypes.object.isRequired,
  plantName: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

export default connect(mapStateToProps, null)(ThemeProvider);
